import type {
  AccountDetail,
  AccountFinancialSummary,
  AccountSummary,
  ActivityDetail,
  ActivitySummary,
  ActivityType,
  ContactDetail,
  ContactSummary,
  ConvertLeadInput,
  ConvertLeadResult,
  CreateAccountInput,
  CreateActivityInput,
  CreateContactInput,
  CreateDealInput,
  CreateLeadInput,
  CreateNoteInput,
  DealDetail,
  DealSummary,
  LeadDetail,
  LeadSummary,
  MyDay,
  NoteSummary,
  Paginated,
  PipelineBoard,
  PipelineSummary,
  RecordRef,
  TimelineEntry,
  UpdateAccountInput,
  UpdateActivityInput,
  UpdateContactInput,
  UpdateDealInput,
  UpdateLeadInput,
} from '@whn/shared';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, toQueryString } from '@/lib/api-client';
import { CRM_QUERY_PREFIXES, queryKeys } from '@/lib/query-client';

type Params = Record<string, string | number | undefined | null>;

/**
 * CRM modules reference one another — an account rolls up its deals, a deal
 * shows its account — so a write anywhere invalidates the lot. Being precise
 * would mean tracking every relationship on every mutation; these queries are
 * small and cached, so the broad sweep is the better trade.
 */
function invalidateCrm(queryClient: QueryClient): void {
  for (const prefix of CRM_QUERY_PREFIXES) {
    void queryClient.invalidateQueries({ queryKey: prefix });
  }
  void queryClient.invalidateQueries({ queryKey: ['timeline'] });
  void queryClient.invalidateQueries({ queryKey: ['notes'] });
}

/** Keeps the previous page on screen while the next one loads. */
function keepPrevious<T>(previous: T): T {
  return previous;
}

// --- Leads -----------------------------------------------------------------

export function useLeads(params: Params) {
  return useQuery({
    queryKey: queryKeys.leads.list(params),
    queryFn: ({ signal }) =>
      api.get<Paginated<LeadSummary>>(`/leads${toQueryString(params)}`, signal),
    placeholderData: keepPrevious,
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.leads.detail(id ?? ''),
    queryFn: ({ signal }) => api.get<LeadDetail>(`/leads/${id}`, signal),
    enabled: Boolean(id),
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLeadInput) => api.post<LeadDetail>('/leads', input),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateLeadInput }) =>
      api.patch<LeadDetail>(`/leads/${id}`, input),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/leads/${id}`),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useConvertLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ConvertLeadInput }) =>
      api.post<ConvertLeadResult>(`/leads/${id}/convert`, input),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useBulkAssignLeads() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; businessManagerId: string }) =>
      api.post<{ requested: number; affected: number; skipped: string[] }>(
        '/leads/bulk/assign',
        input,
      ),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

// --- Accounts --------------------------------------------------------------

export function useAccounts(params: Params) {
  return useQuery({
    queryKey: queryKeys.accounts.list(params),
    queryFn: ({ signal }) =>
      api.get<Paginated<AccountSummary>>(`/accounts${toQueryString(params)}`, signal),
    placeholderData: keepPrevious,
  });
}

export function useAccount(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.accounts.detail(id ?? ''),
    queryFn: ({ signal }) => api.get<AccountDetail>(`/accounts/${id}`, signal),
    enabled: Boolean(id),
  });
}

/**
 * Quotes, Invoices and the Deal pipeline for this account and its contacts.
 * A separate, slower query from `useAccount` so the header never waits on
 * it — see the comment on `AccountFinancialSummary` for why it is bucketed
 * by currency rather than a single number.
 */
export function useAccountFinancialSummary(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.accounts.financialSummary(id ?? ''),
    queryFn: ({ signal }) =>
      api.get<AccountFinancialSummary>(`/accounts/${id}/financial-summary`, signal),
    enabled: Boolean(id),
  });
}

export function useAccountOptions(search: string) {
  return useQuery({
    queryKey: queryKeys.accounts.options(search),
    queryFn: ({ signal }) =>
      api.get<RecordRef[]>(`/accounts/options${toQueryString({ search })}`, signal),
    staleTime: 60_000,
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAccountInput) => api.post<AccountDetail>('/accounts', input),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAccountInput }) =>
      api.patch<AccountDetail>(`/accounts/${id}`, input),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/accounts/${id}`),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useUploadAccountAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      api.upload<AccountDetail>(`/accounts/${id}/attachment`, [file], 'file'),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

/** Adds files to an account, keeping the ones already there. */
export function useAddAccountAttachments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, files }: { id: string; files: File[] }) =>
      api.upload<AccountDetail>(`/accounts/${id}/attachments`, files, 'files'),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useRemoveAccountAttachmentById() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, attachmentId }: { id: string; attachmentId: string }) =>
      api.delete<AccountDetail>(`/accounts/${id}/attachments/${attachmentId}`),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useRemoveAccountAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<AccountDetail>(`/accounts/${id}/attachment`),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

// --- Contacts --------------------------------------------------------------

export function useContacts(params: Params) {
  return useQuery({
    queryKey: queryKeys.contacts.list(params),
    queryFn: ({ signal }) =>
      api.get<Paginated<ContactSummary>>(`/contacts${toQueryString(params)}`, signal),
    placeholderData: keepPrevious,
  });
}

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.contacts.detail(id ?? ''),
    queryFn: ({ signal }) => api.get<ContactDetail>(`/contacts/${id}`, signal),
    enabled: Boolean(id),
  });
}

export function useContactOptions(search: string, accountId?: string) {
  return useQuery({
    queryKey: queryKeys.contacts.options(search, accountId),
    queryFn: ({ signal }) =>
      api.get<RecordRef[]>(`/contacts/options${toQueryString({ search, accountId })}`, signal),
    staleTime: 60_000,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContactInput) => api.post<ContactDetail>('/contacts', input),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateContactInput }) =>
      api.patch<ContactDetail>(`/contacts/${id}`, input),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/contacts/${id}`),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

// --- Deals -----------------------------------------------------------------

export function useDeals(params: Params) {
  return useQuery({
    queryKey: queryKeys.deals.list(params),
    queryFn: ({ signal }) =>
      api.get<Paginated<DealSummary>>(`/deals${toQueryString(params)}`, signal),
    placeholderData: keepPrevious,
  });
}

export function useDeal(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.deals.detail(id ?? ''),
    queryFn: ({ signal }) => api.get<DealDetail>(`/deals/${id}`, signal),
    enabled: Boolean(id),
  });
}

export function useDealOptions(search: string) {
  return useQuery({
    queryKey: queryKeys.deals.options(search),
    queryFn: ({ signal }) =>
      api.get<RecordRef[]>(`/deals/options${toQueryString({ search })}`, signal),
    staleTime: 60_000,
  });
}

export function useBoard(params: Params) {
  return useQuery({
    queryKey: queryKeys.deals.board(params),
    queryFn: ({ signal }) => api.get<PipelineBoard>(`/deals/board${toQueryString(params)}`, signal),
    placeholderData: keepPrevious,
  });
}

export function usePipelines() {
  return useQuery({
    queryKey: queryKeys.pipelines,
    queryFn: ({ signal }) => api.get<PipelineSummary[]>('/pipelines', signal),
    // Pipelines change rarely and are read by nearly every deal screen.
    staleTime: 5 * 60_000,
  });
}

export function useCreateDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDealInput) => api.post<DealDetail>('/deals', input),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useUpdateDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDealInput }) =>
      api.patch<DealDetail>(`/deals/${id}`, input),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useMoveDealStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      stageId,
      lostReason,
    }: {
      id: string;
      stageId: string;
      lostReason?: string;
    }) => api.patch<DealDetail>(`/deals/${id}/stage`, { stageId, lostReason }),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useDeleteDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/deals/${id}`),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

// --- Activities ------------------------------------------------------------

export function useActivities(params: Params) {
  return useQuery({
    queryKey: queryKeys.activities.list(params),
    queryFn: ({ signal }) =>
      api.get<Paginated<ActivitySummary>>(`/activities${toQueryString(params)}`, signal),
    placeholderData: keepPrevious,
  });
}

export function useActivity(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.activities.detail(id ?? ''),
    queryFn: ({ signal }) => api.get<ActivityDetail>(`/activities/${id}`, signal),
    enabled: Boolean(id),
  });
}

export function useMyDay() {
  return useQuery({
    queryKey: queryKeys.activities.myDay,
    queryFn: ({ signal }) => api.get<MyDay>('/activities/my-day', signal),
    // What is due "today" moves as the day goes on.
    staleTime: 30_000,
  });
}

export function useCalendar(params: {
  from: string;
  to: string;
  businessManagerId?: string;
  type?: ActivityType;
}) {
  return useQuery({
    queryKey: queryKeys.activities.calendar(params),
    queryFn: ({ signal }) =>
      api.get<ActivitySummary[]>(`/activities/calendar${toQueryString(params)}`, signal),
    placeholderData: keepPrevious,
  });
}

export function useCreateActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateActivityInput) => api.post<ActivityDetail>('/activities', input),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

/**
 * Ticking a task off is the most frequent write in the product and it always
 * succeeds, so the change is applied to the cache immediately and rolled back
 * if the server disagrees. Without this the checkbox sits visibly unchanged
 * until the refetch lands, which reads as a broken control.
 */
export function useUpdateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateActivityInput }) =>
      api.patch<ActivityDetail>(`/activities/${id}`, input),

    onMutate: async ({ id, input }) => {
      // Stop an in-flight refetch from overwriting the optimistic value.
      await queryClient.cancelQueries({ queryKey: queryKeys.activities.all });
      const snapshot = queryClient.getQueriesData({ queryKey: queryKeys.activities.all });

      const patch = (activity: ActivitySummary): ActivitySummary => {
        if (activity.id !== id) return activity;
        const status = input.status ?? activity.status;
        return {
          ...activity,
          status,
          // Derived fields have to move together, or the row contradicts itself.
          completedAt: status === 'COMPLETED' ? new Date().toISOString() : null,
          isOverdue: status === 'COMPLETED' ? false : activity.isOverdue,
        };
      };

      queryClient.setQueriesData<Paginated<ActivitySummary>>(
        { queryKey: queryKeys.activities.all },
        (current) =>
          current && 'data' in current ? { ...current, data: current.data.map(patch) } : current,
      );

      queryClient.setQueryData<MyDay>(queryKeys.activities.myDay, (current) => {
        if (!current) return current;
        const isDone = input.status === 'COMPLETED';
        const remove = (list: ActivitySummary[]) =>
          isDone ? list.filter((activity) => activity.id !== id) : list.map(patch);
        return {
          overdue: remove(current.overdue),
          dueToday: remove(current.dueToday),
          upcoming: remove(current.upcoming),
          completedToday: current.completedToday + (isDone ? 1 : 0),
        };
      });

      return { snapshot };
    },

    onError: (_error, _variables, context) => {
      // Put back exactly what was there before the optimistic write.
      for (const [key, data] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, data);
      }
    },

    onSettled: () => invalidateCrm(queryClient),
  });
}

export function useDeleteActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/activities/${id}`),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

// --- Notes and timeline ----------------------------------------------------

export function useNotes(params: Params) {
  return useQuery({
    queryKey: queryKeys.notes(params),
    queryFn: ({ signal }) =>
      api.get<Paginated<NoteSummary>>(`/notes${toQueryString(params)}`, signal),
    // Without a parent id this would list every note in the organization.
    enabled: Object.values(params).some(Boolean),
  });
}

export function useCreateNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateNoteInput) => api.post<NoteSummary>('/notes', input),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useDeleteNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/notes/${id}`),
    onSuccess: () => invalidateCrm(queryClient),
  });
}

export function useTimeline(type: 'LEAD' | 'ACCOUNT' | 'CONTACT' | 'DEAL', id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.timeline(type, id ?? ''),
    queryFn: ({ signal }) => api.get<TimelineEntry[]>(`/timeline/${type}/${id}`, signal),
    enabled: Boolean(id),
  });
}
